import DomainManagementFilters from 'civil-status/components/domain-management/list/filters';
import { ActDomainManagmentSearchCrumbs } from 'civil-status/constants/breadcrumb';
import { useFormik } from 'formik';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import ListHeader from 'shell/components/shared/list-header';
import { initialValues } from '..';
import { authsObj, headCells, validationSchemaDomainManagementFilters } from './constants';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { NewHttpResponseList } from 'shell/models/http-response';
import { transformerActDomains } from '../utils/transform-act-domains';
import { ActDomainManagement } from 'shell/models/act-domain-management';
import { transformDataset } from './utils';
import { Typography, useEventCallback } from '@mui/material';
import Table from 'shell/components/shared/table';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import { noop } from 'shell/utils/noop';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { AxiosPromise } from 'axios';
import {
  ActDomainManagementVM,
  ActDomainManagmentFiltersForm,
  ActDomainManagmentResponseVM,
} from 'civil-status/view-models/act-domain-managment-list';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { __ACT_API_URL__ } from 'shell/constants/environment';

const DomainManagementPage = (): React.ReactElement => {
  const navigate = useNavigate();

  const auths = useActionAuthorization(authsObj);

  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<ActDomainManagementVM>>({
    element: null,
    open: false,
  });

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<ActDomainManagementVM>>({
    element: null,
    open: false,
  });

  const filterTable = useFilterTable<ActDomainManagmentFiltersForm>();

  const [actDomains, getActDomains] = useHttpFetch<
    NewHttpResponseList<ActDomainManagement>,
    ActDomainManagmentResponseVM
  >(`${__ACT_API_URL__}/tipo-dominio-atti`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: {},
    },
    transformer: transformerActDomains,
  });

  const deleteActDomain = useHttpClient(`${__ACT_API_URL__}/tipo-dominio-atti/delete/`, {
    options: {
      method: 'DELETE',
    },
  });

  const onSubmit = useCallback(
    (formValues: ActDomainManagmentFiltersForm) => {
      const queryString = transformDataset({ ...formValues, page: 0 });
      getActDomains({
        params: {},
        paramsSerializer: () => queryString,
      });
      filterDomainManagement.setFieldValue('page', 0);
    },
    [getActDomains]
  );

  const filterDomainManagement = useFormik<ActDomainManagmentFiltersForm>({
    initialValues: initialValues,
    validationSchema: validationSchemaDomainManagementFilters,
    onSubmit,
  });

  useEffect(() => {
    filterDomainManagement.setValues(filterTable?.values || initialValues);
    const queryString = transformDataset(filterTable?.values || initialValues);
    getActDomains({
      params: {},
      paramsSerializer: () => queryString,
    });
  }, []);

  const count: number = useMemo(() => {
    return (actDomains?.data as ActDomainManagmentResponseVM)?.pageInfo?.totalCount || 0;
  }, [actDomains?.data]);

  const onVisibilityPress = useCallback(
    (cell): Promise<ActDomainManagementVM> => {
      return new Promise((resolve) => {
        navigate(
          generatePath('/stato-civile/ricerca-dominio/dettaglio/:id', { id: cell.domainType })
        );
        resolve(cell);
      });
    },
    [navigate]
  );

  const onEditPress = useCallback(
    (cell): Promise<ActDomainManagementVM> => {
      return new Promise((resolve) => {
        navigate(
          generatePath('/stato-civile/ricerca-dominio/modifica/:id', { id: cell.domainType })
        );
        resolve(cell);
      });
    },
    [navigate]
  );

  const onTrashPress = useEventCallback(
    (cell: ActDomainManagementVM): Promise<ActDomainManagementVM> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      }) as Promise<ActDomainManagementVM>;
    }
  );

  const handleChangePage = useCallback(
    (newPage: number) => {
      const newDataset = { ...(filterDomainManagement?.values || {}), page: newPage || 0 };
      const queryString = transformDataset(newDataset);
      (
        getActDomains({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<
          NewHttpResponseList<ActDomainManagement>,
          ActDomainManagmentResponseVM
        >
      ).then(() => {
        filterDomainManagement.setFieldValue('page', newPage);
      });
    },
    [getActDomains, filterDomainManagement?.values, filterDomainManagement.setFieldValue]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage: number) => {
      const newDataset = {
        ...(filterDomainManagement?.values || {}),
        rowsPerPage: rowsPerPage || 0,
        page: 0,
      };
      const queryString = transformDataset(newDataset);
      (
        getActDomains({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<
          NewHttpResponseList<ActDomainManagement>,
          ActDomainManagmentResponseVM
        >
      ).then(() => {
        filterDomainManagement.setValues(newDataset);
      });
    },
    [getActDomains, filterDomainManagement.setFieldValue, filterDomainManagement?.values]
  );

  return (
    <>
      <Breadcrumbs items={ActDomainManagmentSearchCrumbs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuovo dominio"
        addBtnLabel="Aggiungi nuovo dominio"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/stato-civile/ricerca-dominio/nuovo');
        }}
        title="Ricerca Dominio"
      />
      <DomainManagementFilters filterDomainManagementForm={filterDomainManagement} />
      {actDomains?.data ? (
        (actDomains?.data as ActDomainManagmentResponseVM)?.actDomains?.length > 0 ? (
          <Table
            onSort={noop}
            head={headCells}
            orders={filterDomainManagement?.values?.orders}
            activeActions={auths}
            page={filterDomainManagement?.values?.page}
            rowsPerPage={filterDomainManagement?.values?.rowsPerPage}
            body={(actDomains?.data as ActDomainManagmentResponseVM)?.actDomains}
            isServer
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            onTrashPress={onTrashPress}
            count={count || 0}
            noSort
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista Domini"
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
      <DeleteAlertModal
        title="Cancellazione Dominio"
        message={`Confermare la cancellazione dell dominio ${
          deleteAlertModal.element?.domainName || ''
        }?`}
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={() => {
          (
            deleteActDomain({
              url: `/${deleteAlertModal?.element?.domainType}`,
            }) as AxiosPromise<void>
          ).then(() => {
            setDeleteAlertModal({ open: false, element: null });
            setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
          });
        }}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Dominio"
        message={`La cancellazione dell dominio ${
          deleteSuccessModal.element?.domainName || ''
        } è avvenuta con successo`}
        onClose={() => {
          const newDataset = {
            ...filterDomainManagement.values,
            rowsPerPage: 10,
            page: 0,
          };
          const queryString = transformDataset(newDataset);
          (
            getActDomains({
              params: {},
              paramsSerializer: () => queryString,
            }) as ResponsePromise<
              NewHttpResponseList<ActDomainManagement>,
              ActDomainManagmentResponseVM
            >
          ).then(() => {
            setDeleteSuccessModal({ open: false, element: null });
          });
        }}
        onOk={() => {
          const newDataset = {
            ...filterDomainManagement.values,
          };
          const queryString = transformDataset(newDataset);
          (
            getActDomains({
              params: {},
              paramsSerializer: () => queryString,
            }) as ResponsePromise<
              NewHttpResponseList<ActDomainManagement>,
              ActDomainManagmentResponseVM
            >
          ).then(() => {
            setDeleteSuccessModal({ open: false, element: null });
          });
        }}
      />
    </>
  );
};

DomainManagementPage.displayName = ' Domain Managment Page';
const ActDomainManagementPage = memo(DomainManagementPage);
export default ActDomainManagementPage;
